
import { type ReactNode, Children, isValidElement, useRef, useState, useCallback } from "react";
import { useLanguage, type DocLanguage } from "./LanguageContext";

interface BlockProps {
  language: DocLanguage;
  children: ReactNode;
}

/**
 * A block of content that only renders when its language matches the selected language.
 * Used inside <LanguageBlock>.
 */
export function Block({ children }: BlockProps) {
  // Rendering is controlled by parent LanguageBlock
  return <>{children}</>;
}

interface LanguageBlockProps {
  children: ReactNode;
}

const LANG_LABELS: Record<string, string> = {
  node: "Node.js",
  curl: "cURL",
};

/**
 * Container that shows only the child <Block> matching the selected language,
 * rendered inside a styled code-tabs container with a copy button.
 */
export default function LanguageBlock({ children }: LanguageBlockProps) {
  const { language } = useLanguage();
  const codeRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const text = codeRef.current?.textContent ?? "";
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const blocks = Children.toArray(children).filter(
    (child) => isValidElement<BlockProps>(child) && child.props.language === language
  );

  let selected: ReactNode;
  if (blocks.length === 0) {
    const all = Children.toArray(children);
    selected = all[0] ?? null;
  } else {
    selected = blocks;
  }

  return (
    <div className="docs-codetabs">
      <div className="docs-codetabs-header">
        <span className="docs-codetabs-tab docs-codetabs-tab--active">
          {LANG_LABELS[language] ?? language}
        </span>
        <button
          onClick={handleCopy}
          className={`docs-codetabs-copy${copied ? " docs-codetabs-copy--copied" : ""}`}
          title="Copy code"
        >
          {copied ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <div ref={codeRef} className="docs-langblock-content">
        {selected}
      </div>
    </div>
  );
}
